import { Modal, toast } from '@pmi/dsm-react';
import { useEffect, useMemo, useState } from 'react';
import AgreementCard from '../../../../../../shared/components/InviteWizard/AgreementCard';
import RenderHtml from '../../../../../../shared/components/RenderHtml';
import useUser from '../../../../../../shared/hooks/useUser';
import { AgreementApiModel } from '../../../../../../shared/models/DealModel';
import { ProductApiModel } from '../../../../../../shared/models/ProductModel';
import { User } from '../../../../../../shared/models/UserModels';
import { dealHasExpired } from '../../../../../../shared/utils/dataHelpers';
import { useReAssignProductMutation } from './apiSlice';
import styles from './reassignProducts.module.scss';

export interface ReassignProductsProps {
  agreements: AgreementApiModel[];
  onCancel: () => void;
  user: User;
  toRemoveProduct: ProductApiModel;
  currentProductAgreement: string;
}

export default function ReassignProducts({
  agreements,
  onCancel,
  toRemoveProduct,
  user,
  currentProductAgreement,
}: ReassignProductsProps) {
  const currentUser = useUser();
  const [reAssign, reAssigned] = useReAssignProductMutation();
  const [submitting, setSubmitting] = useState(false);
  const [animationClassName, setAnimationClassName] = useState('hidden');
  const filteredAgreements: AgreementApiModel[] = useMemo(
    () =>
      agreements.filter((a) =>
        a.products.find((p) => p.sku === toRemoveProduct.sku),
      ),
    [agreements],
  );
  const onSelectAgreement = (agreement: AgreementApiModel) => {
    const newUserProductId = agreement.products.find(
      (p) => p.sku === toRemoveProduct.sku,
    );
    setSubmitting(true);
    if (newUserProductId) {
      reAssign({
        userId: Number(user.id),
        accountProductIdToAssign: newUserProductId.accountProductId,
        accountProductIdToRemove: toRemoveProduct.accountProductId,
        modifiedByUser: {
          id: Number(currentUser.person_id),
          name: `${currentUser.given_name} ${currentUser.family_name}`,
        },
      })
        .unwrap()
        .then(() => {
          toast(
            <span className="fw-normal">
              You have reassigned{' '}
              <RenderHtml html={toRemoveProduct.name} tag="span" /> from{' '}
              {currentProductAgreement} to{' '}
              {agreement.dealName.length > 0
                ? agreement.dealName
                : agreement.id}
            </span>,
            'success',
          );
        })
        .catch(() => {
          toast(
            <span className="fw-normal">
              Failed to reassigned{' '}
              <RenderHtml html={toRemoveProduct.name} tag="span" /> from{' '}
              {currentProductAgreement} to{' '}
              {agreement.dealName.length > 0
                ? agreement.dealName
                : agreement.id}
            </span>,
            'danger',
          );
        })
        .finally(() => {
          reAssigned.reset();
          setSubmitting(false);
          onCancel();
        });
    }
  };
  useEffect(() => {
    setTimeout(() => setAnimationClassName(''), 0);
  }, []);
  return (
    <Modal
      show
      backdrop="static"
      onClose={() => {
        if (!reAssigned.isLoading && !submitting) {
          onCancel();
        }
      }}
      title=""
      variant="dialog"
      customClassName={`${styles.reassignProductsModal} ${animationClassName}`}
    >
      <div className="wizard-container pb-5">
        <div className={`${styles.reassignProducts} py-4`}>
          {filteredAgreements &&
            filteredAgreements.length > 0 &&
            filteredAgreements.map((a) => (
              <AgreementCard
                deal={a}
                title={a.dealName}
                btnLabel="Select Agreement"
                expired={dealHasExpired(a)}
                btnDisabled={reAssigned.isLoading || submitting}
                onClick={() => {
                  onSelectAgreement(a);
                }}
                key={`reassignProductToAgreementSelection${a.id.toString()}`}
              />
            ))}
        </div>
      </div>
    </Modal>
  );
}
